import { sys2LangMap } from '@knowledge/knowledges/SupplementKnowledge/utils';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { overseaTranslate } from 'services/api/overseaTranslate';
import {
  getQueryReplyByConversationId,
  getSupplementKnowledgeDetail,
  getSupplementKnowledgeDetailByMsgId,
  GetSupplementKnowledgeDetailByMsgIdParams,
  getSupplementKnowledgeList,
  GetSupplementKnowledgeListParams,
  getSupplementKnowledgeListSubmittedUser,
  getSupplementKnowledgeOperationHistory,
} from 'services/api/supplementKnowledge';

const getSupplementKnowledgeListWithTranslate = async (
  params: GetSupplementKnowledgeListParams & { language: General.SystemLanguage },
  signal?: AbortSignal,
) => {
  const { language, ...restParams } = params;
  const res = await getSupplementKnowledgeList(restParams, signal);
  const list = res.data.data?.supplementalKnowledgeList ?? [];
  const total = res.data.data?.totalCount ?? 0;
  if (restParams.source === 'CUSTOMER' || restParams.source === 'HISTORY_CUSTOMER') {
    return {
      list,
      total,
    };
  }
  const langKey = sys2LangMap[language.toUpperCase()];
  const needTranslateList = list.reduce(
    (res, { supplementalKnowledgeValue }) => {
      const { rewriteQuestion, question } = supplementalKnowledgeValue;
      if (rewriteQuestion) {
        if (!supplementalKnowledgeValue.rewriteQuestionTranslateMap) {
          supplementalKnowledgeValue.rewriteQuestionTranslateMap = {};
        }
        const { rewriteQuestionTranslateMap } = supplementalKnowledgeValue;
        if (!rewriteQuestionTranslateMap[langKey]) {
          res.push({
            text: rewriteQuestion,
            map: rewriteQuestionTranslateMap,
          });
        }
      } else if (question) {
        if (!question?.translateMap) {
          question.translateMap = {};
        }
        const { translateMap } = question;
        if (!translateMap[langKey]) {
          res.push({
            text: question?.message || '',
            map: translateMap,
          });
        }
      }
      return res;
    },
    [] as {
      text: string;
      map: Record<string, string>;
    }[],
  );
  if (!needTranslateList.length) {
    return {
      list,
      total,
    };
  }
  const texts = needTranslateList.map(({ text }) => text);
  const translated = await overseaTranslate(
    texts,
    language,
    'supplementKnowledge',
    undefined,
    signal,
  );
  translated.forEach((text, index) => {
    needTranslateList[index].map[langKey] = text;
  });
  return { list, total };
};

export const qaPair = createQueryKeys('qaPair', {
  submitMsgIdsRecord: (conversationId: string) => ({
    queryKey: [conversationId],
    queryFn: ({ signal }) => getQueryReplyByConversationId(conversationId, signal),
  }),
  operationHistory: (knowledgeId: string) => ({
    queryKey: [knowledgeId],
    queryFn: ({ signal }) => getSupplementKnowledgeOperationHistory(knowledgeId, signal),
  }),
  knowledgeDetail: (knowledgeId: number) => ({
    queryKey: [knowledgeId],
    queryFn: ({ signal }) => getSupplementKnowledgeDetail(knowledgeId, signal),
  }),
  knowledgeDetailByMsgId: (params: GetSupplementKnowledgeDetailByMsgIdParams) => ({
    queryKey: [params],
    queryFn: ({ signal }) => getSupplementKnowledgeDetailByMsgId(params, signal),
  }),
  knowledgeList: (params: GetSupplementKnowledgeListParams) => ({
    queryKey: [params],
    queryFn: ({ signal }) => getSupplementKnowledgeList(params, signal),
  }),
  knowledgeListWithTranslate: (
    params: GetSupplementKnowledgeListParams & { language: General.SystemLanguage },
  ) => ({
    queryKey: [params],
    queryFn: ({ signal }) => getSupplementKnowledgeListWithTranslate(params, signal),
  }),
  knowledgeListSubmittedUsers: {
    queryKey: null,
    queryFn: ({ signal }) => getSupplementKnowledgeListSubmittedUser(signal),
  },
});
